/**
 * reducers/collections.ts
 * desc: client collections
 * note: connects to third-party systems via Y Meadows API endpoints
 *
 */
import type { Reducer } from '@reduxjs/toolkit';
import { sortAz } from "../../utilities";
import type { CollectionsAction } from '../actions/collections';
import type { CollectionDto, CollectionItemPage, CollectionTableMetadataDto, UploadedCollectionFileDto } from '../../types/api/collections';
import type { ComponentInfo } from '../../types/api/components';
import type { ApiErrorJson } from '../../utilities/error';

export interface CollectionsState {
    isLoading: {
        createCollection: boolean;
        deleteCollection: boolean;
        exportCollection: boolean;
        getCollection: boolean;
        getCollectionItems: boolean;
        getCollectionMetadata: boolean;
        getCollectionUsage: boolean;
        getCollectionUploadedFiles: boolean;
        getCollections: boolean;
        updateCollection: boolean;
        updateCollectionMetadata: boolean;
        uploadCollectionFile: boolean;
    };
    // empty objects until the collection, its item page and its metadata arrive
    detail: CollectionDto | Record<string, never>;
    list: CollectionDto[];
    items: CollectionItemPage | Record<string, never>;
    metadata: CollectionTableMetadataDto | Record<string, never>;
    metadataById: Record<string, CollectionTableMetadataDto>;
    collectionUsage: ComponentInfo[];
    // null until the first fetch; the upload-files screen clears it to an empty list
    uploadedFiles: UploadedCollectionFileDto[] | null;
    uploadFileError: ApiErrorJson | null;
}

const INITIAL_STATE: CollectionsState = {
    isLoading: {
        createCollection: false,
        deleteCollection: false,
        exportCollection: false,
        getCollection: false,
        getCollectionItems: false,
        getCollectionMetadata: false,
        getCollectionUsage: false,
        getCollectionUploadedFiles: false,
        getCollections: true,
        updateCollection: false,
        updateCollectionMetadata: false,
        uploadCollectionFile: false,
    },
    detail: {},
    list: [],
    items: {},
    metadata: {},
    metadataById: {},
    collectionUsage: [],
    uploadedFiles: null,
    uploadFileError: null
};


function collectionsReducer(state: CollectionsState = INITIAL_STATE, action: CollectionsAction): CollectionsState {
    switch (action.type) {
        // CLEAR Collections
        case 'CLEAR_COLLECTIONS':
            return _clearCollections();
        
        // CLEAR Collection Metadata
        case 'CLEAR_COLLECTION_METADATA':
            return _clearCollectionMetadata(state);

        // CLEAR Collection Uploaded Files
        case 'CLEAR_COLLECTION_UPLOADED_FILES':
            return _clearCollectionUploadedFiles(state);

        // CREATE Collection (Instance)
        case 'CREATE_COLLECTION_ERROR':
            return _createCollectionError(state);
        case 'CREATE_COLLECTION_REQUEST':
            return _createCollectionRequest(state);
        case 'CREATE_COLLECTION_SUCCESS':
            return _createCollectionSuccess(state, action.payload);

        // DELETE Collection (Instance)
        case 'DELETE_COLLECTION_ERROR':
            return _deleteCollectionError(state);
        case 'DELETE_COLLECTION_REQUEST':
            return _deleteCollectionRequest(state);
        case 'DELETE_COLLECTION_SUCCESS':
            return _deleteCollectionSuccess(state, action.payload);

        // EXPORT Collection (Instance)
        case 'EXPORT_COLLECTION_ERROR':
            return _exportCollectionError(state);
        case 'EXPORT_COLLECTION_REQUEST':
            return _exportCollectionRequest(state);
        case 'EXPORT_COLLECTION_SUCCESS':
            return _exportCollectionSuccess(state);

        // GET Collection (Instance)
        case 'GET_COLLECTION_ERROR':
            return _getCollectionError(state);
        case 'GET_COLLECTION_REQUEST':
            return _getCollectionRequest(state);
        case 'GET_COLLECTION_SUCCESS':
            return _getCollectionSuccess(state, action.payload);

        // GET Collection Usage (Instance)
        case 'GET_COLLECTION_USAGE_ERROR':
            return _getCollectionUsageError(state);
        case 'GET_COLLECTION_USAGE_REQUEST':
            return _getCollectionUsageRequest(state);
        case 'GET_COLLECTION_USAGE_SUCCESS':
            return _getCollectionUsageSuccess(state, action.payload);

        // GET Collection Uploaded Files
        case 'GET_COLLECTION_UPLOADED_FILES_ERROR':
            return _getCollectionUploadedFilesError(state);
        case 'GET_COLLECTION_UPLOADED_FILES_REQUEST':
            return _getCollectionUploadedFilesRequest(state);
        case 'GET_COLLECTION_UPLOADED_FILES_SUCCESS':
            return _getCollectionUploadedFilesSuccess(state, action.payload);

        // GET Collections (Collection)
        case 'GET_COLLECTIONS_ERROR':
            return _getCollectionsError(state);
        case 'GET_COLLECTIONS_REQUEST':
            return _getCollectionsRequest(state);
        case 'GET_COLLECTIONS_SUCCESS':
            return _getCollectionsSuccess(state, action.payload);

        // GET Collection Items
        case 'GET_COLLECTION_ITEMS_ERROR':
            return _getCollectionItemsError(state);
        case 'GET_COLLECTION_ITEMS_REQUEST':
            return _getCollectionItemsRequest(state);
        case 'GET_COLLECTION_ITEMS_SUCCESS':
            return _getCollectionItemsSuccess(state, action.payload);

        // GET Collection Metadata
        case 'GET_COLLECTION_METADATA_ERROR':
            return _getCollectionMetadataError(state);
        case 'GET_COLLECTION_METADATA_REQUEST':
            return _getCollectionMetadataRequest(state);
        case 'GET_COLLECTION_METADATA_SUCCESS':
            return _getCollectionMetadataSuccess(state, action.payload, action.collectionId);

        // UPDATE Collection Metadata
        case 'UPDATE_COLLECTION_METADATA_ERROR':
            return _updateCollectionMetadataError(state);
        case 'UPDATE_COLLECTION_METADATA_REQUEST':
            return _updateCollectionMetadataRequest(state);
        case 'UPDATE_COLLECTION_METADATA_SUCCESS':
            return _updateCollectionMetadataSuccess(state, action.payload);

        // Update Collection (Instance)
        case 'UPDATE_COLLECTION_ERROR':
            return _updateCollectionError(state);
        case 'UPDATE_COLLECTION_REQUEST':
            return _updateCollectionRequest(state);
        case 'UPDATE_COLLECTION_SUCCESS':
            return _updateCollectionSuccess(state, action.payload);

        // Upload Collection File
        case 'UPLOAD_COLLECTION_FILE_ERROR':
            return _uploadCollectionFileError(state, action.payload);
        case 'UPLOAD_COLLECTION_FILE_REQUEST':
            return _uploadCollectionFileRequest(state);
        case 'UPLOAD_COLLECTION_FILE_SUCCESS':
            return _uploadCollectionFileSuccess(state, action.payload);

        default:
            return state;
    }
}



//*************************************************
//*****   CLEAR Collections     *******************
//*************************************************
function _clearCollections(): CollectionsState {
    return INITIAL_STATE;
}



//*************************************************
//*****   CLEAR Collection Metadata     ***********
//*************************************************
function _clearCollectionMetadata(state: CollectionsState): CollectionsState {
    return {
        ...state,
        metadata: {},
    };
}



//*************************************************
//*****   CLEAR Collection Metadata     ***********
//*************************************************
function _clearCollectionUploadedFiles(state: CollectionsState): CollectionsState {
    return {
        ...state,
        uploadedFiles: [],
        uploadFileError: null,
    };
}



//*************************************************
//*****   CREATE Collection (Instance)   **********
//*************************************************
function _createCollectionError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createCollection: false,
        },
    };
}

function _createCollectionRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createCollection: true,
        },
    };
}

function _createCollectionSuccess(state: CollectionsState, payload: CollectionDto): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            createCollection: false,
        },
        detail: payload,
        list: sortAz([ ...state.list, payload ], 'title'),
    };
}



//*************************************************
//*****   DELETE Collection (Instance)   **************
//*************************************************
function _deleteCollectionError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteCollection: false,
        },
    };
}

function _deleteCollectionRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteCollection: true,
        },
    };
}

function _deleteCollectionSuccess(state: CollectionsState, collectionId: string): CollectionsState {
    // create new list without deleted collection
    const [ ...collectionsList ] = state.list;
    const deletedIndex = state.list.findIndex(x => x.id === collectionId);
    if (deletedIndex > -1) collectionsList.splice(deletedIndex, 1);

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            deleteCollection: false,
        },
        list: sortAz(collectionsList, 'title'),
    };
}



//*************************************************
//*****   EXPORT Collection (Instance)   **********
//*************************************************
function _exportCollectionError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            exportCollection: false,
        },
    };
}

function _exportCollectionRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            exportCollection: true,
        },
    };
}

function _exportCollectionSuccess(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            exportCollection: false,
        }
    };
}



//*************************************************
//*****   GET Collection (Instance)   *****************
//*************************************************
function _getCollectionError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollection: false,
        },
    };
}

function _getCollectionRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollection: true,
        },
    };
}

function _getCollectionSuccess(state: CollectionsState, payload: CollectionDto): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollection: false,
        },
        detail: payload,
    };
}



//*************************************************
//*****   GET Collection Usage (Instance)   ***********
//*************************************************
function _getCollectionUsageError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionUsage: false,
        },
    };
}

function _getCollectionUsageRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionUsage: true,
        },
    };
}

function _getCollectionUsageSuccess(state: CollectionsState, payload: ComponentInfo[]): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionUsage: false,
        },
        collectionUsage: payload,
    };
}



//*************************************************
//*****   GET Collection Uploaded Files   *********
//*************************************************
function _getCollectionUploadedFilesError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionUploadedFiles: false,
        },
    };
}

function _getCollectionUploadedFilesRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionUploadedFiles: true,
        },
    };
}

function _getCollectionUploadedFilesSuccess(state: CollectionsState, payload: UploadedCollectionFileDto[]): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionUploadedFiles: false,
        },
        uploadedFiles: payload,
    };
}



//*************************************************
//*****   GET Collections (Collection)   **********
//*************************************************
function _getCollectionsError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollections: false,
        },
    };
}

function _getCollectionsRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollections: true,
        },
    };
}

function _getCollectionsSuccess(state: CollectionsState, payload: CollectionDto[]): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollections: false,
        },
        list: sortAz(payload, 'title'),
    };
}



//*************************************************
//*****   GET Collection Items           **********
//*************************************************
function _getCollectionItemsError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionItems: false,
        },
    };
}

function _getCollectionItemsRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionItems: true,
        },
    };
}

function _getCollectionItemsSuccess(state: CollectionsState, payload: CollectionItemPage): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionItems: false,
        },
        items: payload,
    };
}



//*************************************************
//*****   GET Collection Metadata        **********
//*************************************************
function _getCollectionMetadataError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionMetadata: false,
        },
    };
}

function _getCollectionMetadataRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionMetadata: true,
        },
    };
}

function _getCollectionMetadataSuccess(state: CollectionsState, payload: CollectionTableMetadataDto, collectionId: string): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            getCollectionMetadata: false,
        },
        metadata: payload,
        // keyed cache alongside the legacy single slot — the review screen reads metadata
        // for several collections at once (one per linked field)
        metadataById: collectionId
            ? { ...state.metadataById, [collectionId]: payload }
            : state.metadataById,
    };
}



//*************************************************
//*****   UPDATE Collection Metadata     **********
//*************************************************
function _updateCollectionMetadataError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateCollectionMetadata: false,
        },
    };
}

function _updateCollectionMetadataRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateCollectionMetadata: true,
        },
    };
}

function _updateCollectionMetadataSuccess(state: CollectionsState, payload: CollectionTableMetadataDto): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateCollectionMetadata: false,
        },
        metadata: payload,
    };
}



//*************************************************
//*****   UPDATE Collection (Instance)   **********
//*************************************************
function _updateCollectionError(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateCollection: false,
        },
    };
}

function _updateCollectionRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateCollection: true,
        },
    };
}

function _updateCollectionSuccess(state: CollectionsState, payload: CollectionDto): CollectionsState {
    let collectionsList = [ ...state.list ];

    // if list exists, modify w updated collection
    // note: list will not exist if deeplinked or refreshed detail page
    if (collectionsList.length) {
        const updatedIndex = collectionsList.findIndex(x => x.id === payload.id);
        if (updatedIndex > -1) collectionsList[updatedIndex] = payload;
        collectionsList = sortAz(collectionsList, 'title');
    }

    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            updateCollection: false,
        },
        detail: payload,
        list: collectionsList,
    };
}



//*************************************************
//*****   UPLOAD Collection File      *************
//*************************************************
function _uploadCollectionFileError(state: CollectionsState, payload: ApiErrorJson): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            uploadCollectionFile: false,
        },
        uploadFileError: payload
    };
}

function _uploadCollectionFileRequest(state: CollectionsState): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            uploadCollectionFile: true,
        },
    };
}

function _uploadCollectionFileSuccess(state: CollectionsState, payload: UploadedCollectionFileDto): CollectionsState {
    return {
        ...state,
        isLoading: {
            ...state.isLoading,
            uploadCollectionFile: false,
        },
        uploadedFiles: [ ...state.uploadedFiles || [], payload ],
    };
}


export default collectionsReducer as Reducer<CollectionsState>;
